import { useState, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/components/ui/use-toast";
import { Upload, FileSpreadsheet, AlertCircle, CheckCircle2, X } from "lucide-react";
import { FieldMappingDialogV3 } from "./FieldMappingDialogV3";
import { MappingConfirmation } from "./MappingConfirmation";
import { PracticeSelectionModal } from "./PracticeSelectionModal";
// import { DataPreview } from "./DataPreview"; // Removed - not needed in new flow
import { useSSE } from "@/hooks/useSSE";
import { useAuth } from "@/contexts/AuthContext";
import { useProgramYear } from "@/contexts/ProgramYearContext";
import { useProgramAccess } from "@/platform/ProgramAccessContext";
import { useActiveProgram } from "@/platform/useActiveProgram";
import { useAco } from "@/contexts/AcoContext";
import { useQueryClient } from "@tanstack/react-query";
import { useUserPractices } from "@/hooks/queries/useMipsQueries";

interface UploadFile {
  file: File;
  status: "pending" | "mapping" | "uploading" | "calculating" | "processing-frequency" | "complete" | "error";
  progress: number;
  rowCount?: number;
  errorMessage?: string;
  warnings?: string[];
  processingInsights?: Record<string, any>;
}

/** R14 Phase E: APP Plus uploads are scoped to ONE collection type. */
const COLLECTION_TYPES = [
  { value: 'Medicare CQM', description: 'CMS beneficiary-scoped data' },
  { value: 'MIPS CQM', description: 'All-payer registry data' },
  { value: 'eCQM', description: 'All-payer EHR data' },
] as const;
type CollectionType = typeof COLLECTION_TYPES[number]['value'];

export function ClaimsUpload() {
  const [files, setFiles] = useState<UploadFile[]>([]);
  const [currentFile, setCurrentFile] = useState<UploadFile | null>(null);
  const [showMapping, setShowMapping] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showPracticeSelection, setShowPracticeSelection] = useState(false);
  const [currentMappings, setCurrentMappings] = useState<Record<string, any>>({});
  const [currentSampleRow, setCurrentSampleRow] = useState<Record<string, any>>({});
  const [detectedTin, setDetectedTin] = useState<string | undefined>();
  const [tinMismatch, setTinMismatch] = useState(false);
  const [selectedPracticeId, setSelectedPracticeId] = useState<number | undefined>();
  const [ignoreTinField, setIgnoreTinField] = useState(false);
  // const [showPreview, setShowPreview] = useState(false); // Removed - not needed
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentUploadId, setCurrentUploadId] = useState<string | null>(null);
  const { toast } = useToast();
  const { user } = useAuth();
  const { programYear } = useProgramYear();
  const queryClient = useQueryClient();
  const practicesQ = useUserPractices();
  const userPractices = practicesQ.data ?? [];
  const practicesPending = practicesQ.isPending && !practicesQ.data;

  // APP Plus mode (D12): when the ACTIVE program is APP Plus, the upload
  // target is the selected ACO (its shadow practice id under the hood).
  // The target list is module-scoped — MIPS sees practices, APP sees ACOs.
  // useActiveProgram (not raw sessionStorage): covers the session pick AND
  // the single-program bypass (APP-only users default here correctly).
  const { hasProgram } = useProgramAccess();
  const { acos, selectedAco, selectAco } = useAco();
  const activeProgram = useActiveProgram();
  const appMode = hasProgram('app-plus')
    && activeProgram === 'app-plus'
    && acos.length > 0;
  const acoTarget = appMode ? (selectedAco ?? acos[0]) : null;

  // R14 Phase E: the declared collection type for APP Plus uploads. Data is
  // scoped to ONE type — it only populates that type's QPS cells. MIPS
  // uploads never send this (the backend rejects it for MIPS practices).
  const [collectionType, setCollectionType] = useState<CollectionType>('Medicare CQM');

  /**
   * Human-readable upload target ("no accidental confusion", owner 2026-07-14):
   * shown in the always-on banner, the mapping-confirmation dialog, and the
   * completion toast.
   */
  const uploadTargetLabel = (practiceId?: number): string | null => {
    if (appMode && acoTarget) {
      return `ACO ${acoTarget.aco_id_cms} — ${acoTarget.name} (APP Plus · ${collectionType}, PY ${programYear})`;
    }
    const pid = practiceId ?? selectedPracticeId;
    const p = userPractices.find((x: any) => x.id === pid)
      ?? (userPractices.length === 1 ? userPractices[0] : null);
    return p ? `${p.practice_alias || p.tin_name} (MIPS, PY ${programYear})` : null;
  };

  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
  
  // SSE hook for real-time progress
  const { progress, error: sseError, isConnected } = useSSE(`${apiUrl}/api/sse/upload-progress`, {
    onMessage: (data) => {
      // Update file progress based on SSE data
      if (currentFile && data.progress !== undefined) {
        updateFileStatus(
          currentFile.file.name, 
          getStatusFromSSE(data.status || "uploading"),
          data.progress,
          data.rowCount,
          data.message,
          data.warnings
        );
      }
    },
    onError: (error) => {
      console.error("[SSE Error]:", error);
      if (currentFile) {
        toast({
          title: "Connection Lost",
          description: "Lost connection to progress updates. Upload continues in background.",
          variant: "default",
        });
      }
    },
    onConnect: () => {
      console.log("[SSE] Connected to progress stream");
    },
  });
  
  // Helper to map SSE status to our file status
  const getStatusFromSSE = (sseStatus: string): UploadFile["status"] => {
    const statusMap: Record<string, UploadFile["status"]> = {
      'processing': 'uploading',
      'calculating': 'calculating',
      'frequency': 'processing-frequency',
      'completed': 'complete',
      'error': 'error',
    };
    return statusMap[sseStatus] || 'uploading';
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const newFiles = acceptedFiles.map(file => ({
      file,
      status: "pending" as const,
      progress: 0
    }));
    setFiles(prev => [...prev, ...newFiles]);
    
    // Start processing the first file if not already processing
    if (!isProcessing && newFiles.length > 0) {
      processNextFile(newFiles[0]);
    }
  }, [isProcessing]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'text/plain': ['.txt'],
    },
    maxSize: 500 * 1024 * 1024, // 500MB limit
  });

  const processNextFile = async (uploadFile: UploadFile) => {
    setIsProcessing(true);
    setCurrentFile(uploadFile);
    
    // Update file status to mapping
    updateFileStatus(uploadFile.file.name, "mapping", 10);
    
    // Always show mapping dialog (no more templates)
    setShowMapping(true);
  };

  // Template checking removed - using learning system instead

  const validateAndUpload = async (
    uploadFile: UploadFile, 
    mappingTemplate: string,
    practiceId?: number,
    shouldIgnoreTin?: boolean
  ) => {
    try {
      // Update status to uploading (skip validation step)
      updateFileStatus(uploadFile.file.name, "uploading", 30);
      
      const formData = new FormData();
      formData.append("file", uploadFile.file);
      formData.append("mappingTemplate", mappingTemplate);
      
      // Add practice ID if provided (passed directly to avoid async state issues)
      const effectivePracticeId = practiceId ?? selectedPracticeId;
      if (effectivePracticeId) {
        formData.append("practiceId", effectivePracticeId.toString());
        console.log('[ClaimsUpload] Sending practiceId:', effectivePracticeId);
      }
      
      // Add ignoreTinField flag if TIN should be ignored
      const effectiveIgnoreTin = shouldIgnoreTin ?? ignoreTinField;
      if (effectiveIgnoreTin) {
        formData.append("ignoreTinField", "true");
      }
      
      // Upload the file directly (validation happens during processing).
      // ?year= targets the year being viewed so uploads land in the right year
      // (and are blocked by the lock middleware if that year is locked).
      // APP Plus (R14): &collectionType= declares the upload's collection-type
      // lane — the batch is stamped and Phase 2 calculates ONLY that type's specs.
      const collectionTypeParam = appMode
        ? `&collectionType=${encodeURIComponent(collectionType)}`
        : '';
      const uploadResponse = await fetch(`${apiUrl}/api/claims/upload?year=${programYear}${collectionTypeParam}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
        },
        body: formData,
      });
      
      if (!uploadResponse.ok) {
        const errorData = await uploadResponse.json();
        const error = errorData.error || errorData;
        throw new Error(error.message || error.details || "Upload failed");
      }
      
      const responseData = await uploadResponse.json();
      const result = responseData.data || responseData;
      
      // Store upload ID for SSE tracking
      if (result.uploadId) {
        setCurrentUploadId(result.uploadId);
      }
      
      // Invalidate upload history query to make the file appear immediately
      queryClient.invalidateQueries({ queryKey: ['upload-history'] });
      
      // If SSE is not connected, update status manually
      if (!isConnected || !result.uploadId) {
        updateFileStatus(uploadFile.file.name, "complete", 100, result.rowCount || result.processedCount);
      }
      
      // Enhanced success message with auto-processing info + the TARGET
      // (owner 2026-07-14: a mis-routed upload should be caught in seconds).
      const targetForToast = uploadTargetLabel(effectivePracticeId as number | undefined);
      let successMessage = `Processed ${result.rowCount || result.processedCount || 0} encounters from ${uploadFile.file.name}`;
      if (targetForToast) {
        successMessage += ` → ${targetForToast}`;
      }
      
      if (result.autoProcessing?.phase2 && result.autoProcessing?.phase3) {
        successMessage += `\n✓ Calculated ${result.autoProcessing.phase2.calculations} measure results`;
        // [B3] Phase 3 (patient outcomes) finishes after this response; don't
        // claim it's done. Gap Management refreshes itself when it completes.
        if (result.autoProcessing.phase3.async || !result.autoProcessing.phase3.outcomes) {
          successMessage += `\n⏳ Patient outcomes are still calculating — Gap Management will update automatically`;
        } else {
          successMessage += `\n✓ Processed ${result.autoProcessing.phase3.outcomes} patient outcomes`;
        }
      }
      
      // Store warnings and insights with the file
      if (result.warnings || result.processingInsights) {
        updateFileStatus(
          uploadFile.file.name, 
          "complete", 
          100, 
          result.rowCount || result.processedCount,
          undefined,
          result.warnings,
          result.processingInsights
        );
      }
      
      // Display warnings if present
      if (result.warnings && result.warnings.length > 0) {
        toast({
          title: "Upload Complete with Warnings",
          description: (
            <div className="space-y-1">
              <p>{successMessage}</p>
              <p className="text-sm text-yellow-600">⚠ {result.warnings.length} warnings encountered</p>
            </div>
          ) as any,
        });
      } else {
        toast({
          title: "Upload & Processing Complete", 
          description: successMessage,
        });
      }
      
      // Display processing insights if available
      if (result.processingInsights) {
        console.log("[Processing Insights]:", result.processingInsights);
        
        // Show recommendations if any
        if (result.processingInsights.recommendations && result.processingInsights.recommendations.length > 0) {
          setTimeout(() => {
            toast({
              title: "Data Quality Recommendations",
              description: (
                <div className="space-y-1">
                  {result.processingInsights.recommendations.slice(0, 2).map((rec: string, idx: number) => (
                    <p key={idx} className="text-sm">• {rec}</p>
                  ))}
                </div>
              ) as any,
              variant: "default",
            });
          }, 2000);
        }
      }
      
      // Process next file if any
      const nextFile = files.find(f => f.status === "pending");
      if (nextFile) {
        setTimeout(() => processNextFile(nextFile), 1000);
      } else {
        setIsProcessing(false);
        setCurrentFile(null);
      }
    } catch (error: any) {
      updateFileStatus(uploadFile.file.name, "error", 0, undefined, error.message);
      toast({
        title: "Upload Failed",
        description: error.message,
        variant: "destructive",
      });
      setIsProcessing(false);
      setCurrentFile(null);
    }
  };

  const updateFileStatus = (
    fileName: string, 
    status: UploadFile["status"], 
    progress: number,
    rowCount?: number,
    errorMessage?: string,
    warnings?: string[],
    processingInsights?: Record<string, any>
  ) => {
    setFiles(prev => prev.map(f => 
      f.file.name === fileName 
        ? { ...f, status, progress, rowCount, errorMessage, warnings, processingInsights }
        : f
    ));
  };

  const removeFile = (fileName: string) => {
    setFiles(prev => prev.filter(f => f.file.name !== fileName));
  };

  const handleMappingComplete = (mappingTemplate: string) => {
    setShowMapping(false);
    const parsed = JSON.parse(mappingTemplate);
    // Handle both old format (just mapping) and new format (mapping + sampleRow)
    if (parsed.mapping && parsed.sampleRow) {
      setCurrentMappings(parsed.mapping);
      setCurrentSampleRow(parsed.sampleRow);
    } else {
      // Fallback for old format
      setCurrentMappings(parsed);
      setCurrentSampleRow({});
    }
    setShowConfirmation(true);
  };

  const handleConfirmationComplete = async () => {
    setShowConfirmation(false);

    // APP Plus mode: the target IS the selected ACO's shadow practice — no
    // TIN-matching or practice-selection flow (participant TINs in an ACO file
    // never match the shadow's A#### pseudo-TIN by design). The file's TIN
    // column is KEPT (shouldIgnoreTin=false): an ACO is many participant TINs
    // and TIN-level performance is derived from it. The server enforces this
    // for aco_shadow targets regardless of the flag.
    if (appMode && acoTarget?.shadow_practice_id && currentFile) {
      setSelectedPracticeId(acoTarget.shadow_practice_id);
      validateAndUpload(currentFile, JSON.stringify(currentMappings), acoTarget.shadow_practice_id, false);
      return;
    }
    
    // Check if TIN was mapped
    const tinMapped = currentMappings.tin || currentMappings.TIN || currentMappings.tax_id;
    
    if (tinMapped && currentFile) {
      // Check if TIN matches any practice
      try {
        // Use the sample row data that was already parsed during mapping
        const response = await fetch(`${apiUrl}/api/claims/check-tin`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
          },
          body: JSON.stringify({
            mappingTemplate: JSON.stringify(currentMappings),
            firstRow: currentSampleRow  // Use the already-parsed sample row
          }),
        });
        
        const responseData = await response.json();
        const tinCheck = responseData.data || responseData;
        console.log('[TIN Check] Response:', tinCheck);
        
        if (tinCheck.hasTin) {
          setDetectedTin(tinCheck.tin);
          
          if (tinCheck.tinMatches) {
            // TIN matches a practice - show confirmation
            setTinMismatch(false);
            setSelectedPracticeId(tinCheck.matchedPractice.id);
            if (userPractices.length > 1) {
              // Show confirmation for multi-practice users
              setShowPracticeSelection(true);
            } else {
              // Auto-proceed for single practice users - pass practiceId directly
              validateAndUpload(currentFile, JSON.stringify(currentMappings), tinCheck.matchedPractice.id);
            }
          } else {
            // TIN doesn't match any practice - show selection with warning
            setTinMismatch(true);
            setIgnoreTinField(true);
            setShowPracticeSelection(true);
          }
        } else {
          // No TIN in file
          handleNoTinFlow();
        }
      } catch (error) {
        console.error('Error checking TIN:', error);
        // Fall back to normal flow
        handleNoTinFlow();
      }
    } else {
      // No TIN mapped
      handleNoTinFlow();
    }
  };
  
  const handleNoTinFlow = () => {
    setDetectedTin(undefined);
    setTinMismatch(false);
    
    if (userPractices.length > 1) {
      // Multiple practices, no TIN - show selection
      setShowPracticeSelection(true);
    } else if (userPractices.length === 1) {
      // Single practice - auto-select and pass practiceId directly
      const practiceId = userPractices[0].id;
      setSelectedPracticeId(practiceId);
      if (currentFile) {
        validateAndUpload(currentFile, JSON.stringify(currentMappings), practiceId);
      }
    } else if (currentFile) {
      // Let backend handle (no practices available)
      validateAndUpload(currentFile, JSON.stringify(currentMappings));
    }
  };
  
  const handlePracticeConfirm = (practiceId: number, ignoreTin?: boolean) => {
    setSelectedPracticeId(practiceId);
    setIgnoreTinField(ignoreTin || false);
    setShowPracticeSelection(false);
    if (currentFile) {
      // Pass practiceId and ignoreTin directly to avoid async state issues
      validateAndUpload(currentFile, JSON.stringify(currentMappings), practiceId, ignoreTin);
    }
  };

  const handleEditMapping = () => {
    setShowConfirmation(false);
    setShowMapping(true);
  };

  return (
    <div className="space-y-6">
      {/* Upload Zone */}
      <Card>
        <CardHeader>
          <CardTitle>Upload Claims Data</CardTitle>
          <CardDescription>
            {appMode ? (
              <>Data is scoped to <span className="font-semibold">one collection type</span> — it will only populate that collection type's cells in QPS Management.</>
            ) : (
              <>Upload CSV, Excel, or text files containing claims or encounter data.
              Files will be validated and mapped automatically.</>
            )}
          </CardDescription>
          {/* Always-on upload-target banner (owner 2026-07-14: "no accidental confusion") */}
          {appMode && acoTarget ? (
            <div className="mt-2 flex flex-wrap items-center gap-2 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2">
              <Badge className="bg-primary text-primary-foreground">APP PLUS</Badge>
              <span className="text-sm font-medium">
                Uploading for ACO: <span className="text-primary font-semibold">{acoTarget.aco_id_cms}</span> — {acoTarget.name}
                <span className="text-muted-foreground"> · PY {programYear}</span>
              </span>
              {acos.length > 1 && (
                <select
                  className="ml-auto rounded-md border bg-background px-2 py-1 text-xs"
                  value={acoTarget.id}
                  onChange={(e) => selectAco(Number(e.target.value))}
                >
                  {acos.map(a => (
                    <option key={a.id} value={a.id}>{a.aco_id_cms} — {a.name}</option>
                  ))}
                </select>
              )}
            </div>
          ) : (
            <div className="mt-2 flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 min-h-[40px]">
              <Badge variant="secondary" className="bg-slate-700 text-white hover:bg-slate-700">MIPS</Badge>
              {practicesPending ? (
                <span className="inline-block h-4 w-72 animate-pulse rounded bg-slate-200" aria-hidden />
              ) : userPractices.length === 1 ? (
                <span className="text-sm font-medium">
                  Uploading for practice: <span className="font-semibold">{userPractices[0].practice_alias || userPractices[0].tin_name}</span>
                  <span className="text-muted-foreground"> · TIN {userPractices[0].tin} · PY {programYear}</span>
                </span>
              ) : (
                <span className="text-sm font-medium">
                  {userPractices.length} practices
                  <span className="text-muted-foreground"> — the target is confirmed during upload (TIN match or your selection) · PY {programYear}</span>
                </span>
              )}
            </div>
          )}
        </CardHeader>
        <CardContent>
          {/* R14 Phase E: collection-type selector (APP Plus only) — mockup
              layout: cards, dropzone, then ONE scope-reminder line. */}
          {appMode && (
            <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-3" role="radiogroup" aria-label="Collection type">
              {COLLECTION_TYPES.map(ct => {
                const selected = collectionType === ct.value;
                return (
                  <button
                    key={ct.value}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    disabled={isProcessing}
                    onClick={() => setCollectionType(ct.value)}
                    className={`flex items-start gap-3 rounded-lg border p-3 text-left transition-colors
                      ${selected
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border bg-background hover:border-primary/50"}
                      ${isProcessing ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
                  >
                    <span className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border
                      ${selected ? "border-primary-foreground" : "border-muted-foreground"}`}>
                      {selected && <span className="h-2 w-2 rounded-full bg-primary-foreground" />}
                    </span>
                    <span>
                      <span className="block text-sm font-semibold">{ct.value}</span>
                      <span className={`block text-xs ${selected ? "text-primary-foreground/80" : "text-muted-foreground"}`}>
                        {ct.description}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          )}
          <div
            {...getRootProps()}
            className={`
              border-2 border-dashed rounded-lg p-8 text-center cursor-pointer
              transition-colors hover:border-primary
              ${isDragActive ? "border-primary bg-primary/5" : "border-border"}
            `}
          >
            <input {...getInputProps()} />
            <Upload className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-lg font-semibold mb-2">
              {isDragActive ? "Drop files here..." : "Drag & drop files here"}
            </p>
            <p className="text-sm text-muted-foreground mb-4">
              or click to browse files
            </p>
            <p className="text-xs text-muted-foreground">
              Supported formats: CSV, Excel (.xls, .xlsx), Text (.txt) • Max size: 500MB
            </p>
          </div>
          {appMode && (
            <div className="mt-3 flex items-center gap-2 rounded-md border bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
              <AlertCircle className="h-3.5 w-3.5 shrink-0" />
              <span>
                {collectionType} uploads will never populate{' '}
                {COLLECTION_TYPES.filter(ct => ct.value !== collectionType).map(ct => ct.value).join(' or ')} cells.
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* File List */}
      {files.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Upload Queue</CardTitle>
            <CardDescription>
              Files being processed for upload
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {files.map(file => (
              <div key={file.file.name} className="border rounded-lg p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <FileSpreadsheet className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium">{file.file.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {(file.file.size / 1024 / 1024).toFixed(2)} MB
                        {file.rowCount && ` • ${file.rowCount.toLocaleString()} rows`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={
                      file.status === "complete" ? "default" :
                      file.status === "error" ? "destructive" :
                      "secondary"
                    }>
                      {file.status === "pending" && "Pending"}
                      {file.status === "mapping" && "Mapping Fields"}
                      {file.status === "uploading" && "Processing Data"}
                      {file.status === "calculating" && "Calculating Measures"}
                      {file.status === "processing-frequency" && "Applying Frequency Logic"}
                      {file.status === "complete" && "Complete"}
                      {file.status === "error" && "Error"}
                    </Badge>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeFile(file.file.name)}
                      disabled={file.status === "uploading"}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                
                {file.status !== "pending" && file.status !== "complete" && (
                  <div className="space-y-1">
                    <Progress value={file.progress} className="h-2" />
                    {progress?.message && currentFile?.file.name === file.file.name && (
                      <p className="text-xs text-muted-foreground">
                        {progress.message}
                      </p>
                    )}
                  </div>
                )}
                
                {file.errorMessage && (
                  <Alert variant="destructive" className="mt-2">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      <div className="space-y-1">
                        <p>{file.errorMessage}</p>
                        {/* Warnings are shown in the complete status section instead */}
                      </div>
                    </AlertDescription>
                  </Alert>
                )}
                
                {file.status === "complete" && (
                  <div className="space-y-2 mt-2">
                    <div className="flex items-center gap-2 text-sm text-green-600">
                      <CheckCircle2 className="h-4 w-4" />
                      Successfully uploaded and processed
                    </div>
                    
                    {/* Display processing insights for completed files */}
                    {file.processingInsights && (
                      <details className="text-xs text-muted-foreground">
                        <summary className="cursor-pointer font-medium">
                          Processing Details
                        </summary>
                        <div className="mt-1 space-y-1 pl-2">
                          <p>Quality Score: {file.processingInsights.dataQualityScore}%</p>
                          {file.processingInsights.duplicatesMerged > 0 && (
                            <p>Duplicates Merged: {file.processingInsights.duplicatesMerged}</p>
                          )}
                          {file.processingInsights.autoCorrections?.length > 0 && (
                            <p>Auto-corrections: {file.processingInsights.autoCorrections.length}</p>
                          )}
                        </div>
                      </details>
                    )}
                    
                    {/* Display warnings for completed files */}
                    {file.warnings && file.warnings.length > 0 && (
                      <details className="text-xs text-yellow-600">
                        <summary className="cursor-pointer font-medium">
                          {file.warnings.length} Warnings
                        </summary>
                        <ul className="mt-1 space-y-1 pl-2">
                          {file.warnings.slice(0, 3).map((warning, idx) => (
                            <li key={idx}>• {warning}</li>
                          ))}
                          {file.warnings.length > 3 && (
                            <li className="text-muted-foreground">...and {file.warnings.length - 3} more</li>
                          )}
                        </ul>
                      </details>
                    )}
                  </div>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Field Mapping Dialog */}
      {showMapping && currentFile && (
        <FieldMappingDialogV3
          file={currentFile.file}
          onComplete={handleMappingComplete}
          onCancel={() => {
            setShowMapping(false);
            updateFileStatus(currentFile.file.name, "error", 0, undefined, "Mapping cancelled");
            setCurrentFile(null);
            setIsProcessing(false);
          }}
        />
      )}

      {/* Mapping Confirmation Dialog */}
      {showConfirmation && currentFile && (
        <MappingConfirmation
          file={currentFile.file}
          mappings={currentMappings}
          targetLabel={appMode && acoTarget
            ? uploadTargetLabel()
            : userPractices.length === 1 ? uploadTargetLabel(userPractices[0].id) : null}
          targetButton={appMode && acoTarget
            ? `ACO ${acoTarget.aco_id_cms} (APP Plus)`
            : userPractices.length === 1
              ? `${userPractices[0].practice_alias || userPractices[0].tin_name} (MIPS)`
              : null}
          targetProgram={appMode && acoTarget ? 'app-plus' : userPractices.length === 1 ? 'mips' : null}
          onConfirm={handleConfirmationComplete}
          onEdit={handleEditMapping}
          onCancel={() => {
            setShowConfirmation(false);
            updateFileStatus(currentFile.file.name, "error", 0, undefined, "Process cancelled");
            setCurrentFile(null);
            setIsProcessing(false);
          }}
        />
      )}

      {/* Practice Selection Modal */}
      <PracticeSelectionModal
        open={showPracticeSelection}
        onClose={() => {
          setShowPracticeSelection(false);
          setTinMismatch(false);
          setIgnoreTinField(false);
          if (currentFile) {
            updateFileStatus(currentFile.file.name, "error", 0, undefined, "Practice selection cancelled");
            setCurrentFile(null);
            setIsProcessing(false);
          }
        }}
        onConfirm={handlePracticeConfirm}
        detectedTin={detectedTin}
        tinMismatch={tinMismatch}
        userPractices={userPractices}
      />
    </div>
  );
}
